<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="analysis-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row class="pr-3">
          <b-col class="pr-0">
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="analyte.sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('analysis_sample_type', $event)"
            />
          </b-col>
          <b-col class="pr-0">
            <select-filter
              variant="info"
              :icon="analyte.icon"
              placeholder="All Analytes"
              :collection="analyte.filters"
              value-key="id"
              text-key="name"
              filter-field="analyte_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('analysis_analyte', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="analyte" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['analyte']" :field="'name'">
          <template #existing-content>
            <router-link
              :to="`/analytes/${getData(row, {
                levels: ['analyte'],
                field: 'id',
              })}`"
            >
              {{ formatAnalyteName(row.analyte) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="name" slot-scope="{ row }">
        <a :href="`#/analysis/${row.id}`" class="btn-link">
          {{ row.measurement_method || row.name }}
        </a>
      </span>

      <template slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit analysis')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/analysis/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete analysis')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </template>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import AnalysisCollection from "@/collections/lab/AnalysisCollection";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import SelectFilter from "@/components/shared/SelectFilter";
import AnalyteModel from "@/models/lab/AnalyteModel";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData, formatAnalyteName, formatAnalysisName } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "AnalysisServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ModalTrashBasket,
    ServerTable,
    SelectFilter,
  },
  props: {
    collection: {
      type: Object,
      default: () => new AnalysisCollection(),
    },
    analytes: {
      type: Object,
      default: () => null,
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    instance_instrument_id: {
      type: String,
      default: null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "analyte",
        "name",
        // "measurement_method",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => [
        "name",
        // "measurement_method",
      ],
    },
    task_type: {
      type: String,
      default: null,
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit analysis") ||
      this.checkPermission("delete analysis");

    return {
      entity_type: "analysis",
      analyte_id: this.$route.params.analyte_id || null,
      analyte_collection: null,
      instrument_id: this.$route.params.instrument_id || null,
      prep_task_id: this.$route.params.prep_task_id || null,
      test_task_id: this.$route.params.test_task_id || null,
      sample_type_collection: null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            name: "Analysis Name",
          },
        },
      }),
      table_data: {
        sample_type: ["analyte", "sample_type"],
      },
    };
  },
  computed: {
    ...mapGetters({
      trash: "analysisTrash",
    }),
    analyte() {
      return {
        icon: `fa-${AnalyteModel.iconStatic()}`,
        filters: this.analytes || this.analyte_collection,
      };
    },
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    show_modal() {
      return (
        this.checkPermission("delete analysis") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    formatAnalyteName(analyte) {
      return formatAnalyteName(analyte);
    },
    formatAnalysisName(analyte) {
      return formatAnalysisName(analyte);
    },
    
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    async init() {
      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      !this.analytes &&
        (this.analyte_collection = await getCollection({
          ...payload,
          collection_type: "analyte",
        }));
      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
        }));
    },
    prepareCollectionFilter(filter) {
      this.analyte_id && filter.filterEquals("analyte_id", this.analyte_id);
      this.task_type &&
        filter.filterEquals(
          `${this.task_type}_task_analyses.${this.task_type}_task_id`,
          this.$route.params[`${this.task_type}_task_id`]
        );
      (this.instrument_id || this.instance_instrument_id) &&
        filter.filterEquals(
          "instrument_tests.instrument_id",
          this.instrument_id || this.instance_instrument_id
        );

      return filter;
    },
  },
  created() {
    this.init();
  },
};
</script>
